/**
 * "Did this `geometry/create-arraypath` dispatch actually create anything, and
 * if not, what does the user read about it?" — the ONE place the shell's
 * ARRAYPATH wording is decided, for both halves of that question.
 *
 * ## The defect this exists to close
 *
 * `./geometryShellReducer.ts`'s arm wrote `summary.arraypath_created` in its
 * ordinary success tone on EVERY dispatch, while
 * `./arrayReducers.ts::handleCreateArrayPath` returns the document UNCHANGED
 * down eight separate paths: the path id resolves to nothing; it resolves to a
 * type ARRAYPATH does not take; it resolves to a heavy `POLYLINE` subtype the
 * command declines; its curve is not confined to a plane parallel to WCS XY;
 * `divide` was given `itemCount < 2`; `measure` was given `itemSpacing <= 0`;
 * the path is shorter than that spacing; the total would exceed MAXARRAY; or a
 * caller-supplied `arrayGroupId` already names a foreign group. Each of those
 * reached the user through nothing but a `console.warn`, under a row that said
 * copies had been made. CLAUDE.md ПРИОРИТЕТ #1 forbids exactly that.
 *
 * ## Why the floor verdict is the RESULT, not a list of reasons
 *
 * The obvious repair — one audit key per refusal path — is the wrong shape
 * twice over. It requires the shell to re-derive a decision the document
 * reducer already makes (two truths, guaranteed to drift), and it is
 * open-ended: a ninth guard added to `handleCreateArrayPath` would silently
 * fall back to claiming success, which is the very defect again. So the
 * question asked underneath everything is the only one that cannot go stale —
 * DID THE DOCUMENT GAIN ENTITIES? — and it is answered from the dispatch's own
 * declared `EntityMutation`, the single diff `commitMutation` and history
 * already consume. No second diff is computed anywhere (skill
 * `state-mutation-integrity`: audit is a CONSUMER of the mutation outcome).
 *
 * Where the reason IS known exactly, it still wins: the two `command-engine`
 * predicates ARRAYPATH's own eligibility is defined by
 * (`heavyPolylineCurveRefusal.ts` for the curve's SUBTYPE,
 * `arrayPathPlaneRefusal.ts` for its PLANE) are pure, cost nothing, and are
 * asked BEFORE the mutation by {@link arrayPathDeclinedAudit}, exactly where
 * they always were. The neutral row is the floor under everything else.
 *
 * ## The two halves and why they sit on opposite sides of the reducer
 *
 * `reduceEditorShellState` runs BEFORE `reduceGeometryDocumentWithMutations`
 * (see `./editorStore.ts`'s dispatch body), so at shell time the real
 * `EntityMutation` does not exist yet. An earlier revision of this module
 * closed that gap by running `handleCreateArrayPath` a SECOND time,
 * speculatively, and throwing the result away — correct but a genuine
 * regression: it doubled the array construction on every SUCCESSFUL ARRAYPATH,
 * up to MAXARRAY (100,000) items, and emitted the reducer's `console.warn`
 * twice. {@link applyArrayPathOutcomeToAudit} replaces it with the free form:
 * the dispatch seam already holds `dispatchMutations`, so the outcome row is
 * settled there, at zero extra work, from the number that is already exact.
 *
 * The audit ROW ORDER is untouched by the move. The shell reducer still pushes
 * exactly one row for this command, at the same point in the sequence; this
 * module only rewrites that row's text and tone in place, inside the same
 * `set()` updater, before any observer sees the state.
 */

import type {
  DynamicBlockCommand,
  EditorCommand,
  EditorShellState,
} from "@dxf-ai-editor/contracts";
import {
  arrayPathPlaneRefusal,
  heavyPolylineCurveRefusalFor,
} from "@dxf-ai-editor/command-engine";
import {
  getEntityById,
  type DrawingDocument,
} from "@dxf-ai-editor/drawing-model";

import { t } from "../i18n";
// `isPathEntity` — the document reducer's OWN narrowing, imported rather than
// re-derived so the frame predicate can never be asked of a curve kind
// `handleCreateArrayPath` never reaches.
import { isPathEntity } from "./arrayReducers";
import {
  arrayPathHeavyPolylineRefusalAudit,
  arrayPathPlaneRefusalAudit,
} from "./geometryShellHeavyPolylineAudit";

type CreateArrayPathCommand = Extract<
  EditorCommand,
  { type: "geometry/create-arraypath" }
>;

/**
 * The NAMED refusal line for this ARRAYPATH dispatch, or `null` when the
 * command's two eligibility predicates both accept the path curve.
 *
 * Pure and pre-mutation by design: `geometryDocument` is the document
 * `reduceGeometryShellCommand` is handed, and both predicates only read it.
 * Order is `handleCreateArrayPath`'s own guard order, so the row names the
 * reason the document reducer actually stops at:
 *
 *  1. the heavy `POLYLINE` SUBTYPE ARRAYPATH cannot read as a curve;
 *  2. the PLANE its sampler's two-axis answer would be unfaithful in.
 *
 * Everything else this function deliberately does NOT answer — it cannot see
 * those reasons, and guessing at them is how the shell and the document reducer
 * drift apart. That case is the floor row
 * {@link applyArrayPathOutcomeToAudit} lays down from the real mutation.
 */
export function arrayPathDeclinedAudit(
  geometryDocument: DrawingDocument,
  command: CreateArrayPathCommand,
): string | null {
  // 1 — the heavy POLYLINE subtype (@codex PR #785 round 14, P2).
  const declinedSubtype = arrayPathHeavyPolylineRefusalAudit(
    heavyPolylineCurveRefusalFor(
      geometryDocument.spaces,
      command.pathEntityId,
      "array-path",
    ),
  );
  if (declinedSubtype) return declinedSubtype;

  // 2 — the plane the curve lives in (@codex PR #785 round 16, P2). The
  // narrowing is `isPathEntity`, the same one the document reducer applies, so
  // a path id that resolves to nothing or to a non-curve type falls through to
  // the outcome row rather than being answered with a frame verdict about a
  // curve that never was one.
  const pathEntity = getEntityById(geometryDocument, command.pathEntityId);
  return pathEntity !== null && isPathEntity(pathEntity)
    ? arrayPathPlaneRefusalAudit(arrayPathPlaneRefusal(pathEntity))
    : null;
}

/**
 * Correct this dispatch's ARRAYPATH audit row once the real result is known:
 * when the document gained NO entities, the shell's optimistic
 * `summary.arraypath_created` row becomes the neutral warning row instead.
 *
 * Called from `./editorStore.ts`'s dispatch updater with `addedCount =
 * dispatchMutations.added.length` — the dispatch's own declared
 * `EntityMutation`, not a diff recomputed here. Returns `state` untouched for
 * every other command, and for an ARRAYPATH that really did create copies.
 *
 * ## What it will and will not rewrite
 *
 * Only the row THIS dispatch just pushed, and only if that row claims success:
 *
 *  - `sequence` must be the one the shell reducer allocated in this same
 *    updater (`auditSequenceBefore`), so a state whose newest row belongs to an
 *    earlier dispatch is never touched;
 *  - `tone` must be `"success"`, which is what keeps
 *    {@link arrayPathDeclinedAudit}'s two NAMED refusals intact — they were
 *    pushed as `"warning"` and carry a reason this function does not have, so
 *    overwriting them with the neutral text would lose information.
 *
 * The rewrite is positional-free (it matches the ring entry by `sequence` and
 * the transcript row by its `tx-${sequence}` id) and shape-preserving: only
 * `summary` / `tone` / `message` change, so no field is added or removed and
 * the already-validated state stays valid without a re-parse.
 */
export function applyArrayPathOutcomeToAudit(
  state: EditorShellState,
  command: EditorCommand | DynamicBlockCommand,
  addedCount: number,
  auditSequenceBefore: number,
): EditorShellState {
  if (command.type !== "geometry/create-arraypath") return state;
  if (addedCount > 0) return state;

  const row = state.audit.events.find(
    (event) => event.sequence === auditSequenceBefore,
  );
  if (!row || row.tone !== "success") return state;

  const summary = t("audit.arraypath_nothing_created");
  const transcriptId = `tx-${auditSequenceBefore}`;
  return {
    ...state,
    commandLine: {
      ...state.commandLine,
      transcript: state.commandLine.transcript.map((entry) =>
        entry.id === transcriptId
          ? { ...entry, tone: "warning" as const, message: summary }
          : entry,
      ),
    },
    audit: {
      ...state.audit,
      events: state.audit.events.map((event) =>
        event.sequence === auditSequenceBefore
          ? { ...event, tone: "warning" as const, summary }
          : event,
      ),
    },
  };
}
